import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../button";
import TableWrapper from "../TableWrapper";
import { useGetAllRoles } from "../../../hooks/common/query";
import { Role } from "../../../constants/common";
import { UseFormSetValue } from "react-hook-form";
import { cn } from "../../../utils/cn";
import { useTranslation } from "react-i18next";

interface RolesTableProps {
  selectedRoleId: number;
  setSelectedRoleId: Dispatch<SetStateAction<number>>;
  setValue: UseFormSetValue<{
    roleNameEn: string;
    roleNameAr: string;
  }>;
}

const RolesTable = ({
  selectedRoleId,
  setSelectedRoleId,
  setValue,
}: RolesTableProps) => {
  const { t } = useTranslation();

  const { data: roles } = useGetAllRoles();
  const [selectedKey, setSelectedKey] = useState("");

  const handleRoleSelection = useCallback(
    (roleId: number, roleName: string, roleNameAr: string, key: string) => {
      setSelectedRoleId(roleId);
      setValue("roleNameEn", roleName);
      setValue("roleNameAr", roleNameAr);
      setSelectedKey(key);
    },
    [setSelectedRoleId, setValue]
  );

  const rolesColumns: ColumnDef<Role>[] = useMemo(
    () => [
      {
        accessorKey: "s_RoleNameAr",
        header: t("roleNameArabic"),
        cell: ({ row }) => {
          return <span>{row.original.s_RoleNameAr}</span>;
        },
      },
      {
        accessorKey: "s_RoleName",
        header: t("roleNameEnglish"),
        cell: ({ row }) => {
          return <span>{row.original.s_RoleName}</span>;
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Button
              onClick={() =>
                handleRoleSelection(
                  row.original.n_RoleID,
                  row.original.s_RoleName,
                  row.original.s_RoleNameAr,
                  "n_RoleID"
                )
              }
              type="button"
              className={cn("py-0.5 px-3 min-w-[80px]")}
            >
              {selectedRoleId === row.original.n_RoleID
                ? t("selected")
                : t("select")}
            </Button>
          );
        },
      },
    ],
    [handleRoleSelection, selectedRoleId, t]
  );

  return (
    <TableWrapper
      data={roles}
      columns={rolesColumns}
      isPagination={false}
      isColumnChooser={false}
      selectedKey={selectedKey}
      selectedRowId={selectedRoleId}
    />
  );
};

export default React.memo(RolesTable);
